import {
  addDays,
  addHours,
  addSeconds,
  differenceInHours,
  differenceInSeconds,
  max,
  min,
  startOfDay,
  subDays,
  subHours,
} from 'date-fns'

import { getSpeedThresholdColor } from '@/feature/observatory/utils/marker-constants'

import { GRADIENT_STEPS, lerpColor } from '../Replay/Replay.utils'

import {
  MarkerPoint,
  SelectorBounds,
  SpeedGraphSegment,
  TimelineMarkerKind,
  TimeRangeSelectorProps,
} from './ReplayTimeRange.types'

/** Maximum speed (km/h) used to scale the speed graph height. */
export const MAX_SPEED = 120

/** Fraction (0-1) of the timeline a point sits at, measured from the range start. */
export const getPointPosition = (when: Date, rangeStartTime: Date, periodInSec: number) =>
  differenceInSeconds(new Date(when), rangeStartTime, { roundingMethod: 'ceil' }) / periodInSec

/** Percentage offsets and drag clamps (in cqw units) for the selection window handles. */
export const getSelectorBounds = ({
  selectionStartTime,
  selectionEndTime,
  rangeStartTime,
  rangeEndTime,
  periodInSec,
}: TimeRangeSelectorProps): SelectorBounds => {
  // Adjusted period to account for the 1 hour buffer on each side
  const adjustedPeriodInSec = periodInSec - 60 * 60

  const leftOffset =
    (differenceInSeconds(selectionStartTime, rangeStartTime) / adjustedPeriodInSec) * 100
  const leftRightClamp =
    (differenceInSeconds(subHours(selectionEndTime, 1), rangeStartTime) / adjustedPeriodInSec) * 100
  const leftClamp =
    Math.max(
      differenceInSeconds(subDays(subHours(selectionEndTime, 1), 1), rangeStartTime) /
        adjustedPeriodInSec,
      0,
    ) * 100

  const rightOffset =
    (differenceInSeconds(rangeEndTime, selectionEndTime) / adjustedPeriodInSec) * 100
  const rightLeftClamp =
    (differenceInSeconds(rangeEndTime, addHours(selectionStartTime, 1)) / adjustedPeriodInSec) * 100
  const rightClamp =
    Math.max(
      differenceInSeconds(rangeEndTime, addDays(addHours(selectionStartTime, 1), 1)) /
        adjustedPeriodInSec,
      0,
    ) * 100

  return { leftOffset, leftClamp, leftRightClamp, rightOffset, rightClamp, rightLeftClamp }
}

/** One marker per hour across the range; every 24th (aligned to midnight) is a day marker. */
export const buildTimelineMarkers = (
  rangeStartTime: Date,
  rangeEndTime: Date,
): TimelineMarkerKind[] => {
  const timeDiff = differenceInHours(rangeEndTime, rangeStartTime, { roundingMethod: 'ceil' })
  const offset = differenceInHours(rangeStartTime, startOfDay(rangeStartTime), {
    roundingMethod: 'ceil',
  })

  return Array.from({ length: timeDiff }, (_, i) => ((i + offset) % 24 === 0 ? 'day' : 'hour'))
}

export const getSpeedGraphY = (speed: number | undefined) =>
  100 - Math.min(((speed ?? 0) / MAX_SPEED) * 100, 100)

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

export const buildSpeedGraphSegments = (
  points: MarkerPoint[],
  rangeStartTime: Date,
  periodInSec: number,
): SpeedGraphSegment[] => {
  const getX = (point: MarkerPoint) =>
    getPointPosition(point.when, rangeStartTime, periodInSec) * 100

  const segments: SpeedGraphSegment[] = []
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i]
    const p2 = points[i + 1]

    const x1 = getX(p1)
    const y1 = getSpeedGraphY(p1.speed)
    const x2 = getX(p2)
    const y2 = getSpeedGraphY(p2.speed)

    const colorA = getSpeedThresholdColor(p1.speed)
    const colorB = getSpeedThresholdColor(p2.speed)

    if (colorA === colorB) {
      segments.push({ x1, y1, x2, y2, color: colorA })
      continue
    }

    for (let s = 0; s < GRADIENT_STEPS; s++) {
      const t1 = s / GRADIENT_STEPS
      const t2 = (s + 1) / GRADIENT_STEPS
      segments.push({
        x1: lerp(x1, x2, t1),
        y1: lerp(y1, y2, t1),
        x2: lerp(x1, x2, t2),
        y2: lerp(y1, y2, t2),
        color: lerpColor(colorA, colorB, (t1 + t2) / 2),
      })
    }
  }
  return segments
}

type DragComputation = {
  selectionStartTime: Date
  selectionEndTime: Date
  rangeStartTime: Date
  rangeEndTime: Date
  periodInSec: number
  deltaPercent: number
}

/** Selection may not shrink below 1 hour, grow past 25 hours, or leave the range. */
export const computeDraggedStartTime = ({
  selectionStartTime,
  selectionEndTime,
  rangeStartTime,
  periodInSec,
  deltaPercent,
}: Omit<DragComputation, 'rangeEndTime'>): Date => {
  const newStartTime = addSeconds(selectionStartTime, periodInSec * deltaPercent)
  return max([
    min([newStartTime, subHours(selectionEndTime, 1)]),
    rangeStartTime,
    subHours(selectionEndTime, 25),
  ])
}

export const computeDraggedEndTime = ({
  selectionStartTime,
  selectionEndTime,
  rangeEndTime,
  periodInSec,
  deltaPercent,
}: Omit<DragComputation, 'rangeStartTime'>): Date => {
  const newEndTime = addSeconds(selectionEndTime, periodInSec * deltaPercent)
  return min([
    max([newEndTime, addHours(selectionStartTime, 1)]),
    rangeEndTime,
    addHours(selectionStartTime, 25),
  ])
}

/** Moves the whole selection window, clamping flush against the range edges without resizing. */
export const computeDraggedRange = ({
  selectionStartTime,
  selectionEndTime,
  rangeStartTime,
  rangeEndTime,
  periodInSec,
  deltaPercent,
}: DragComputation): { start: Date; end: Date } => {
  const duration = differenceInSeconds(selectionEndTime, selectionStartTime)
  let newStartTime = addSeconds(selectionStartTime, periodInSec * deltaPercent)
  let newEndTime = addSeconds(selectionEndTime, periodInSec * deltaPercent)

  if (newStartTime < rangeStartTime) {
    newStartTime = rangeStartTime
    newEndTime = addSeconds(rangeStartTime, duration)
  }
  if (newEndTime > rangeEndTime) {
    newEndTime = rangeEndTime
    newStartTime = addSeconds(rangeEndTime, -duration)
  }

  return { start: newStartTime, end: newEndTime }
}
